import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {Promise<void> | null} */
  #hydratePromise = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  async toggle(event) {
    // Native <details> togglet `open` tijdens de click; na async hydrate zou
    // isOpen dan true zijn en close() i.p.v. open() aanroepen.
    event?.preventDefault?.();

    const shouldOpen = !this.isOpen;
    if (shouldOpen) {
      await this.#ensureHydratedIfNeeded();
      return this.open(event);
    }
    return this.close();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  async open(event) {
    await this.#ensureHydratedIfNeeded();

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      const innerSubmenu = details.querySelector(':scope > .menu-drawer__submenu .menu-drawer__inner-submenu');
      if (innerSubmenu instanceof HTMLElement) {
        innerSubmenu.scrollTop = 0;
      }
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * @returns {Promise<void>}
   */
  #ensureHydratedIfNeeded = async () => {
    if (this.dataset.menuDrawerLazy !== 'true') return;
    if (this.dataset.menuDrawerLoaded === 'true') return;
    if (this.#hydratePromise) return this.#hydratePromise;

    this.#hydratePromise = this.#hydrateFromSection();
    try {
      await this.#hydratePromise;
    } finally {
      this.#hydratePromise = null;
    }
  };

  #hydrateFromSection = async () => {
    const rawSectionId = this.dataset.headerSectionId;
    const hydrateKey = this.dataset.menuHydrateKey?.trim();
    if (!rawSectionId || !hydrateKey) return;

    const menuDrawer = this.querySelector('[data-header-drawer]');
    if (!(menuDrawer instanceof HTMLElement)) return;

    this.classList.add('header-drawer--hydrating');
    menuDrawer.setAttribute('aria-busy', 'true');

    try {
      const { sectionRenderer, normalizeSectionId, buildSectionSelector } = await import('@theme/section-renderer');
      const sectionNormalized = normalizeSectionId(rawSectionId);
      const html = await sectionRenderer.getSectionHTML(sectionNormalized, false);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sectionEl = doc.getElementById(buildSectionSelector(sectionNormalized));

      if (!sectionEl) {
        throw new Error('[HeaderDrawer] Section wrapper not found in fetched HTML');
      }

      let freshDrawer = sectionEl.querySelector(
        `header-drawer[data-menu-hydrate-key="${CSS.escape(hydrateKey)}"]`,
      );

      // SRA-response rendert geen lazy attrs; val terug op enige header-drawer in de sectie.
      if (!(freshDrawer instanceof HTMLElement)) {
        const drawers = sectionEl.querySelectorAll('header-drawer');
        if (drawers.length === 1 && drawers[0] instanceof HTMLElement) {
          freshDrawer = drawers[0];
        }
      }

      if (!(freshDrawer instanceof HTMLElement)) {
        throw new Error('[HeaderDrawer] Matching header-drawer not found in section response');
      }

      const freshMenuDrawer = freshDrawer.querySelector('[data-header-drawer]');
      if (!(freshMenuDrawer instanceof HTMLElement)) {
        throw new Error('[HeaderDrawer] Menu drawer body not found in section response');
      }

      menuDrawer.replaceChildren(...Array.from(freshMenuDrawer.childNodes));

      this.dataset.menuDrawerLoaded = 'true';
      this.classList.remove('header-drawer--lazy-shell');
      this.#setupAnimatedElementListeners();
    } finally {
      this.classList.remove('header-drawer--hydrating');
      menuDrawer.removeAttribute('aria-busy');
    }
  };

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
